import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';
import '../styles/Pages.css';

interface ProfileFormData {
  name: string;
  childName: string;
  childAge: string;
  favoriteThemes: string[];
}

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    name: 'Parent',
    childName: 'Little One',
    childAge: '5',
    favoriteThemes: ['Adventure', 'Magic']
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const availableThemes = [
    'Adventure', 'Magic', 'Fantasy', 'Space', 
    'Underwater', 'Dinosaurs', 'Princesses', 'Superheroes',
    'Animals', 'Nature', 'Science', 'History'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleThemeToggle = (theme: string) => {
    setFormData(prev => {
      const themes = [...prev.favoriteThemes];
      if (themes.includes(theme)) {
        return {
          ...prev,
          favoriteThemes: themes.filter(t => t !== theme)
        };
      } else {
        return {
          ...prev,
          favoriteThemes: [...themes, theme]
        };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Mock API call to update profile
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      logger.error('Error updating profile:', error);
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred while updating your profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <h1 className="page-title">Your Profile</h1>
      
      <div className="profile-grid">
        <div className="profile-card main-profile">
          <div className="profile-avatar">
            {formData.name.charAt(0).toUpperCase()}
          </div>
          <div className="profile-header">
            <h2>{formData.name || user?.email?.split('@')[0]}</h2>
            <p className="profile-email">{user?.email}</p>
          </div>
        </div>

        <form className="profile-card" onSubmit={handleSubmit}>
          <h2 className="profile-section-title">Profile Information</h2>
          
          <div className="profile-form-group">
            <label htmlFor="name">Your Name</label>
            <input 
              type="text" 
              id="name" 
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="profile-input"
              placeholder="Your name"
            />
          </div>
          
          <div className="profile-form-group">
            <label htmlFor="childName">Child's Name</label>
            <input 
              type="text" 
              id="childName" 
              name="childName"
              value={formData.childName}
              onChange={handleInputChange}
              className="profile-input"
              placeholder="Child's name"
            />
            <p className="input-help-text">Used to personalize stories</p>
          </div>
          
          <div className="profile-form-group">
            <label htmlFor="childAge">Child's Age</label>
            <input 
              type="number" 
              id="childAge" 
              name="childAge"
              value={formData.childAge}
              onChange={handleInputChange}
              className="profile-input"
              min="1"
              max="12"
              placeholder="Age"
            />
          </div>

          <h3 className="profile-subsection-title">Favorite Story Themes</h3>
          <p className="input-help-text">Select themes your child enjoys</p>
          
          <div className="theme-tags">
            {availableThemes.map(theme => (
              <div 
                key={theme}
                className={`theme-tag ${formData.favoriteThemes.includes(theme) ? 'selected' : ''}`}
                onClick={() => handleThemeToggle(theme)}
              >
                {theme}
              </div>
            ))}
          </div>
          
          <div className="profile-actions">
            <button 
              type="submit" 
              className="action-button primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
            {success && <span className="profile-saved-message">Profile updated successfully!</span>}
            {errorMessage && <span className="profile-error-message">{errorMessage}</span>}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile; 